import { s as HttpHeader } from "./createFetchClient-CkHdS_IG.js";
import { t as createOpenapiFetchClient } from "./createOpenapiFetchClient-DXiMv9WL.js";
//#region src/clients/developerPortal/admin/admin.ts
/** Keboola staff only. Every method 403s for a non-admin token; the UI gates on `custom:isAdmin`. */
const createAdmin = (client) => {
	const getStacks = async (signal) => {
		const { data } = await client.get("/admin/stacks", {}, { signal });
		return data;
	};
	const getChanges = async (query = {}, signal) => {
		const { data } = await client.get("/admin/changes", { query }, { signal });
		return data;
	};
	const getApps = async (query = {}, signal) => {
		const { data } = await client.get("/admin/apps", { query }, { signal });
		return data;
	};
	const getApp = async (id, signal) => {
		const { data } = await client.get("/admin/apps/{id}", { path: { id } }, { signal });
		return data;
	};
	const updateApp = async (id, body, signal) => {
		const { data } = await client.patch("/admin/apps/{id}", {
			path: { id },
			body
		}, { signal });
		return data;
	};
	const publishApp = async (id, signal) => {
		const { data } = await client.post("/admin/apps/{id}/publish", { path: { id } }, { signal });
		return data;
	};
	const rejectApp = async (id, body, signal) => {
		const { data } = await client.post("/admin/apps/{id}/reject", {
			path: { id },
			body
		}, { signal });
		return data;
	};
	const getUsers = async (query = {}, signal) => {
		const { data } = await client.get("/admin/users", { query }, { signal });
		return data;
	};
	const deleteUser = async (username, signal) => {
		await client.delete("/admin/users/{username}", { path: { username } }, { signal });
	};
	/** Clears the user's MFA enrolment so they can re-enrol — the account-recovery path. */
	const resetUserMfa = async (username, signal) => {
		await client.delete("/admin/users/{username}/mfa", { path: { username } }, { signal });
	};
	const addUserToVendor = async (username, vendor, signal) => {
		await client.post("/admin/users/{username}/vendors/{vendor}", { path: {
			username,
			vendor
		} }, { signal });
	};
	const removeUserFromVendor = async (username, vendor, signal) => {
		await client.delete("/admin/users/{username}/vendors/{vendor}", { path: {
			username,
			vendor
		} }, { signal });
	};
	const grantUserAdmin = async (username, signal) => {
		await client.post("/admin/users/{username}/admin", { path: { username } }, { signal });
	};
	const revokeUserAdmin = async (username, signal) => {
		await client.delete("/admin/users/{username}/admin", { path: { username } }, { signal });
	};
	const getVendors = async (query = {}, signal) => {
		const { data } = await client.get("/admin/vendors", { query }, { signal });
		return data;
	};
	const createVendor = async (body, signal) => {
		const { data } = await client.post("/admin/vendors", { body }, { signal });
		return data;
	};
	const updateVendor = async (vendor, body, signal) => {
		const { data } = await client.patch("/admin/vendors/{vendor}", {
			path: { vendor },
			body
		}, { signal });
		return data;
	};
	const deleteVendor = async (vendor, signal) => {
		await client.delete("/admin/vendors/{vendor}", { path: { vendor } }, { signal });
	};
	/** Swaps a pending `_v`-prefixed id for the permanent one carried in the body. */
	const approveVendor = async (vendor, body, signal) => {
		await client.post("/admin/vendors/{vendor}/approve", {
			path: { vendor },
			body
		}, { signal });
	};
	return {
		getStacks,
		getChanges,
		getApps,
		getApp,
		updateApp,
		publishApp,
		rejectApp,
		getUsers,
		deleteUser,
		resetUserMfa,
		addUserToVendor,
		removeUserFromVendor,
		grantUserAdmin,
		revokeUserAdmin,
		getVendors,
		createVendor,
		updateVendor,
		deleteVendor,
		approveVendor
	};
};
//#endregion
//#region src/clients/developerPortal/apps/apps.ts
const createApps = ({ client, publicClient }) => {
	/** The public catalog: scoped server-side to published components. */
	const getPublishedApps = async (query = {}, signal) => {
		const { data } = await publicClient.get("/apps", { query }, { signal });
		return data;
	};
	const getPublishedApp = async (app, signal) => {
		const { data } = await publicClient.get("/apps/{app}", { path: { app } }, { signal });
		return data;
	};
	const getApps = async (vendor, query = {}, signal) => {
		const { data } = await client.get("/vendors/{vendor}/apps", {
			path: { vendor },
			query
		}, { signal });
		return data;
	};
	const getApp = async (vendor, app, signal) => {
		const { data } = await client.get("/vendors/{vendor}/apps/{app}", { path: {
			vendor,
			app
		} }, { signal });
		return data;
	};
	const createApp = async (vendor, body, signal) => {
		const { data } = await client.post("/vendors/{vendor}/apps", {
			path: { vendor },
			body
		}, { signal });
		return data;
	};
	const updateApp = async (vendor, app, body, signal) => {
		const { data } = await client.patch("/vendors/{vendor}/apps/{app}", {
			path: {
				vendor,
				app
			},
			body
		}, { signal });
		return data;
	};
	const deleteApp = async (vendor, app, signal) => {
		await client.delete("/vendors/{vendor}/apps/{app}", { path: {
			vendor,
			app
		} }, { signal });
	};
	const requestAppPublishing = async (vendor, app, signal) => {
		const { data } = await client.post("/vendors/{vendor}/apps/{app}/publish", { path: {
			vendor,
			app
		} }, { signal });
		return data;
	};
	const deprecateApp = async (vendor, app, body, signal) => {
		const { data } = await client.post("/vendors/{vendor}/apps/{app}/deprecate", {
			path: {
				vendor,
				app
			},
			body
		}, { signal });
		return data;
	};
	const getAppVersions = async (vendor, app, query = {}, signal) => {
		const { data } = await client.get("/vendors/{vendor}/apps/{app}/versions", {
			path: {
				vendor,
				app
			},
			query
		}, { signal });
		return data;
	};
	const getAppVersion = async (vendor, app, version, signal) => {
		const { data } = await client.get("/vendors/{vendor}/apps/{app}/versions/{version}", { path: {
			vendor,
			app,
			version
		} }, { signal });
		return data;
	};
	const rollbackAppVersion = async (vendor, app, version, signal) => {
		await client.post("/vendors/{vendor}/apps/{app}/versions/{version}/rollback", { path: {
			vendor,
			app,
			version
		} }, { signal });
	};
	const getAppRepository = async (vendor, app, signal) => {
		const { data } = await client.get("/vendors/{vendor}/apps/{app}/repository", { path: {
			vendor,
			app
		} }, { signal });
		return data;
	};
	/** Requests a presigned S3 upload link; the upload itself is {@link uploadAppIcon}. */
	const createAppIconUploadLink = async (vendor, app, signal) => {
		const { data } = await client.post("/vendors/{vendor}/apps/{app}/icon", { path: {
			vendor,
			app
		} }, { signal });
		return data;
	};
	return {
		getPublishedApps,
		getPublishedApp,
		getApps,
		getApp,
		createApp,
		updateApp,
		deleteApp,
		requestAppPublishing,
		deprecateApp,
		getAppVersions,
		getAppVersion,
		rollbackAppVersion,
		getAppRepository,
		createAppIconUploadLink
	};
};
/**
* Uploads the icon to the presigned URL from `createAppIconUploadLink`. Deliberately a bare `fetch`:
* the URL carries its own signature in the query string, and sending our `Authorization` header to
* S3 makes it reject the request.
*/
const uploadAppIcon = async (uploadUrl, png, signal) => {
	const response = await fetch(uploadUrl, {
		method: "PUT",
		body: png,
		headers: { "Content-Type": "image/png" },
		signal
	});
	if (!response.ok) throw new Error(`Icon upload failed: ${response.status} ${response.statusText}`);
};
//#endregion
//#region src/clients/developerPortal/tokens.ts
/** Raw token, no `Bearer` prefix — see the spec's `bearerAuth` scheme (`type: apiKey`). */
const authHeader = (token) => ({ [HttpHeader.AUTHORIZATION]: token });
//#endregion
//#region src/clients/developerPortal/auth/auth.ts
const isMfaChallengeResponse = (result) => !("token" in result);
const createAuth = ({ client, publicClient }) => {
	const login = async (body, signal) => {
		const { data } = await publicClient.post("/auth/login", { body }, { signal });
		return data;
	};
	const signUp = async (body, signal) => {
		await publicClient.post("/auth/signup", { body }, { signal });
	};
	const confirmAccount = async (body, signal) => {
		await publicClient.post("/auth/confirm", { body }, { signal });
	};
	/** Confirms via the emailed link's path segments, for a consumer echoing that link. */
	const confirmAccountByCode = async (email, code, signal) => {
		await publicClient.post("/auth/confirm/{email}/{code}", { path: {
			email,
			code
		} }, { signal });
	};
	/**
	* Rotates the trio, authenticated with the **refresh** token, on the auth-free client so a
	* rotation triggered by the auth middleware cannot recurse into it. The response carries no new
	* refresh token, so the caller carries the one it has forward.
	*/
	const refreshTokens = async (refreshToken, signal) => {
		const { data } = await publicClient.get("/auth/token", {}, {
			headers: authHeader(refreshToken),
			signal
		});
		return data;
	};
	/** Authenticated with the **access** token. Clear local state regardless of the outcome. */
	const logout = async (accessToken, signal) => {
		await publicClient.post("/auth/logout", {}, {
			headers: authHeader(accessToken),
			signal
		});
	};
	const getProfile = async (signal) => {
		const { data } = await client.get("/auth/profile", {}, { signal });
		return data;
	};
	const requestPasswordReset = async (email, signal) => {
		await publicClient.post("/auth/forgot/{email}", { path: { email } }, { signal });
	};
	const confirmPasswordReset = async (email, body, signal) => {
		await publicClient.post("/auth/forgot/{email}/confirm", {
			path: { email },
			body
		}, { signal });
	};
	const setupMfa = async ({ idToken, accessToken }, signal) => {
		const { data } = await publicClient.post("/auth/mfa", { body: { accessToken } }, {
			headers: authHeader(idToken),
			signal
		});
		return data;
	};
	const confirmMfa = async ({ idToken, accessToken, code }, signal) => {
		await publicClient.post("/auth/mfa/confirm", { body: {
			accessToken,
			code
		} }, {
			headers: authHeader(idToken),
			signal
		});
	};
	return {
		login,
		signUp,
		confirmAccount,
		confirmAccountByCode,
		refreshTokens,
		logout,
		getProfile,
		requestPasswordReset,
		confirmPasswordReset,
		setupMfa,
		confirmMfa
	};
};
//#endregion
//#region src/clients/developerPortal/vendors/vendors.ts
const createVendors = ({ client, publicClient }) => {
	const getVendors = async (query = {}, signal) => {
		const { data } = await publicClient.get("/vendors", { query }, { signal });
		return data;
	};
	const getVendor = async (vendor, signal) => {
		const { data } = await publicClient.get("/vendors/{vendor}", { path: { vendor } }, { signal });
		return data;
	};
	/** Self-service registration. The vendor is pending, under a temporary `_v`-prefixed id. */
	const createVendor = async (body, signal) => {
		const { data } = await client.post("/vendors", { body }, { signal });
		return data;
	};
	const updateVendor = async (vendor, body, signal) => {
		const { data } = await client.patch("/vendors/{vendor}", {
			path: { vendor },
			body
		}, { signal });
		return data;
	};
	const getVendorUsers = async (vendor, query = {}, signal) => {
		const { data } = await client.get("/vendors/{vendor}/users", {
			path: { vendor },
			query
		}, { signal });
		return data;
	};
	/** Requests membership of a vendor for the signed-in user. */
	const requestVendorMembership = async (vendor, signal) => {
		await client.post("/vendors/{vendor}/users", { path: { vendor } }, { signal });
	};
	const approveVendorUser = async (vendor, username, signal) => {
		await client.post("/vendors/{vendor}/users/{username}", { path: {
			vendor,
			username
		} }, { signal });
	};
	const removeVendorUser = async (vendor, username, signal) => {
		await client.delete("/vendors/{vendor}/users/{username}", { path: {
			vendor,
			username
		} }, { signal });
	};
	const getVendorUserRequests = async (vendor, query = {}, signal) => {
		const { data } = await client.get("/vendors/{vendor}/user-requests", {
			path: { vendor },
			query
		}, { signal });
		return data;
	};
	const sendVendorInvitation = async (vendor, email, signal) => {
		await client.post("/vendors/{vendor}/invitations/{email}", { path: {
			vendor,
			email
		} }, { signal });
	};
	/** `security: []` — the invitee follows the emailed link before they have a session. */
	const acceptVendorInvitation = async (vendor, email, code, signal) => {
		await publicClient.get("/vendors/{vendor}/invitations/{email}/{code}", { path: {
			vendor,
			email,
			code
		} }, { signal });
	};
	/** `service=1` filters the member list; there is no distinct endpoint. */
	const getServiceAccounts = async (vendor, query = {}, signal) => {
		const { data } = await client.get("/vendors/{vendor}/users", {
			path: { vendor },
			query: {
				...query,
				service: 1
			}
		}, { signal });
		return data;
	};
	/** The returned `password` is shown once and is not retrievable afterwards. */
	const createServiceAccount = async (vendor, body, signal) => {
		const { data } = await client.post("/vendors/{vendor}/credentials", {
			path: { vendor },
			body
		}, { signal });
		return data;
	};
	return {
		getVendors,
		getVendor,
		createVendor,
		updateVendor,
		getVendorUsers,
		requestVendorMembership,
		approveVendorUser,
		removeVendorUser,
		getVendorUserRequests,
		sendVendorInvitation,
		acceptVendorInvitation,
		getServiceAccounts,
		createServiceAccount
	};
};
//#endregion
//#region src/clients/developerPortal/developerPortalClient.ts
/**
* Percent-encodes a path parameter, leaving the RFC 3986 segment characters `$&+,;=:@` raw.
*
* - apps-api never decodes a path parameter, so `%40` fails its email validation.
*/
const encodePathParam = (value) => encodeURIComponent(value).replace(/%(24|26|2B|2C|3B|3D|3A|40)/g, (match) => decodeURIComponent(match));
/**
* The Developer Portal (apps-api) client; types generated via `redocly.yaml` / `pnpm gen:types`.
*
* - Two fetch clients: `security: []` operations go out unauthenticated, and four use the access or
*   refresh token instead of the id token.
*/
const createDeveloperPortalClient = ({ baseUrl, middlewares = [], authMiddleware }) => {
	const publicClient = createOpenapiFetchClient({
		baseUrl,
		middlewares,
		encodePathParam
	});
	const client = authMiddleware ? createOpenapiFetchClient({
		baseUrl,
		middlewares: [authMiddleware, ...middlewares],
		encodePathParam
	}) : publicClient;
	const healthCheck = async (signal) => {
		const { data } = await publicClient.get("/", {}, { signal });
		return data;
	};
	return {
		healthCheck,
		auth: createAuth({
			client,
			publicClient
		}),
		vendors: createVendors({
			client,
			publicClient
		}),
		apps: createApps({
			client,
			publicClient
		}),
		admin: createAdmin(client)
	};
};
//#endregion
export { uploadAppIcon as i, isMfaChallengeResponse as n, authHeader as r, createDeveloperPortalClient as t };

//# sourceMappingURL=developerPortalClient-Bb2suezr.js.map